import React, { useEffect, useRef, useState } from "react";
import { Card, CheckLabel, EmptyState, LongTextLabel, Page, QueryResult, } from "@sscs/ui";
import { FolderIcon } from "@sscs/ui/icons";
import { getClassName, humanReadableBytes } from "@sscs/utils";
import { Outlet, useParams, useSearchParams } from "react-router";
import { Virtuoso } from "react-virtuoso";
import { FILE_BROWSER_ROUTES, REPORT_PAGES_ROUTES, REPORT_QUERY_PARAMS, } from "../../constants";
import { LinkToReportTab } from "../common";
import { useLevels } from "../hooks";
import { useGetFilesQuery } from "../services/fileBrowser.api";
import { getQueryStatus } from "../utils";
import styles from "./fileBrowser.module.scss";
import FileIcon from "./FileIcon";
import LocationLedger from "./LocationLedger";
import { useGetFileAndReportId } from "./useGetFileAndReportId";
const ItemDetails = ({ item }) => {
    const { scanLevel, isLevelCustomized } = useLevels();
    if (item.type === "DIRECTORY")
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles["file-type"] }, "Folder"),
            React.createElement("div", { className: styles["file-size"] }, "--"),
            React.createElement("div", { className: styles["quality-status"] }, "--")));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles["file-type"] },
            item.file.type,
            "/",
            item.file.subtype),
        React.createElement("div", { className: styles["file-size"] }, humanReadableBytes(item.file.size, 0)),
        React.createElement(CheckLabel, { className: styles["quality-status"], scanLevel: scanLevel, status: item.file.quality_status, isLevelCustomized: isLevelCustomized })));
};
const BrowserItem = ({ item, path, searchParamEntries, index, }) => {
    var _a;
    const { fileId } = useParams();
    const isSelected = item.type === "FILE" && fileId === ((_a = item.file) === null || _a === void 0 ? void 0 : _a.id);
    const classes = getClassName(styles.item, {
        [styles["selected-item"]]: isSelected,
        [styles.even]: index % 2 === 0,
        [styles.odd]: index % 2 === 1,
    });
    const itemLinkPath = item.type === "DIRECTORY" ? `${path}${item.name}/` : path;
    const itemLinkSearch = new URLSearchParams([
        ...searchParamEntries,
        [REPORT_QUERY_PARAMS.path, itemLinkPath],
    ]).toString();
    const itemLinkPathname = item.type === "DIRECTORY"
        ? REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE
        : `${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${item.file.id}/${FILE_BROWSER_ROUTES.INFO_ROUTE}`;
    // A directory gets no leading status icon: its folder glyph is part of the
    // link, so clicking the glyph navigates into the folder.
    const itemIcon = item.type === "FILE" ? (React.createElement(FileIcon, { classification: item.file.classification, deleted: item.file.deleted, verified: item.file.verified, fontSize: "small" })) : null;
    return (React.createElement("div", { className: classes },
        React.createElement("div", { className: styles["item-name"] },
            itemIcon,
            React.createElement(LinkToReportTab, { id: item.name, to: {
                    pathname: itemLinkPathname,
                    search: itemLinkSearch,
                }, disabled: isSelected, color: "link", className: styles["item-link"] },
                item.type === "DIRECTORY" && React.createElement(FolderIcon, null),
                React.createElement(LongTextLabel, { label: item.name, maxCharacters: 80, inline: true })),
            item.file && item.file.order !== null && item.file.order > 0 && (React.createElement("span", null,
                "(",
                item.file.order,
                ")"))),
        React.createElement("div", { className: styles["item-details"] },
            React.createElement(ItemDetails, { item: item }))));
};
const getIndexByFileId = (id, folderContents) => {
    if (!folderContents || !folderContents.items) {
        return 0;
    }
    const index = folderContents.items.findIndex((item) => {
        var _a;
        return ((_a = item.file) === null || _a === void 0 ? void 0 : _a.id) === id;
    });
    if (index !== -1) {
        return index;
    }
    return 0;
};
const FileBrowser = () => {
    var _a;
    var _b, _c;
    const { reportId, fileId } = useGetFileAndReportId();
    const [searchParams] = useSearchParams();
    const [showFolderContents, setShowFolderContents] = useState(true);
    const path = searchParams.get(REPORT_QUERY_PARAMS.path);
    const { data: folderContents, isFetching, isLoading, isError, error, } = useGetFilesQuery({
        path: path !== null && path !== void 0 ? path : "",
        reportId: reportId,
    });
    const searchParamEntries = Array.from(searchParams.entries()).filter(([key]) => key !== REPORT_QUERY_PARAMS.path);
    const maxVisibleItems = 8;
    const itemHeight = parseInt(styles.itemHeight);
    const itemCount = (_b = folderContents === null || folderContents === void 0 ? void 0 : folderContents.items.length) !== null && _b !== void 0 ? _b : 0;
    const maxItemsHeightInPx = itemHeight * maxVisibleItems;
    const totalItemsHeightInPx = itemHeight * itemCount;
    const height = `${Math.min(maxItemsHeightInPx, totalItemsHeightInPx)}px`;
    const isScrollingRequired = totalItemsHeightInPx > maxItemsHeightInPx;
    const showOverlay = isScrollingRequired && showFolderContents;
    const fadeOverlayRef = useRef(null);
    const itemsCounterRef = useRef(null);
    const handleCounterChange = (range) => {
        if (itemsCounterRef.current) {
            itemsCounterRef.current.textContent = `Showing ${range.startIndex + 1}-${range.endIndex + 1} out of ${itemCount} items`;
        }
    };
    // because the fade overlay behaves like sticky footer, we need to ensure it does not overlap the scrollbar
    // so we need to position it correctly
    useEffect(() => {
        var _a;
        if (showOverlay && fadeOverlayRef.current) {
            const scroller = (_a = fadeOverlayRef.current.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector("[data-virtuoso-scroller]");
            const observer = new ResizeObserver(() => {
                if (!scroller || !(fadeOverlayRef === null || fadeOverlayRef === void 0 ? void 0 : fadeOverlayRef.current))
                    return;
                const scrollbarWidth = scroller.offsetWidth - scroller.clientWidth;
                // here we are batching the writes here because the previously separate calls were causing a flicker
                fadeOverlayRef.current.style.cssText += `
              right: ${scrollbarWidth}px;
              visibility: visible;
            `;
            });
            observer.observe(scroller);
            return () => {
                observer.disconnect();
            };
        }
    }, [showOverlay]);
    const handleHtmlElementVisibilityChange = (nodeRef, isVisible) => {
        if (nodeRef) {
            nodeRef.style.visibility = isVisible ? "visible" : "hidden";
        }
    };
    const selectedFileName = fileId !== undefined
        ? ((_c = (_a = folderContents === null || folderContents === void 0 ? void 0 : folderContents.items.find((item) => { var _a; return ((_a = item.file) === null || _a === void 0 ? void 0 : _a.id) === fileId; })) === null || _a === void 0 ? void 0 : _a.name) !== null && _c !== void 0 ? _c : null)
        : null;
    return (React.createElement(Page.Root, null,
        React.createElement(Page.Header, { title: "Components" }),
        React.createElement(Page.Body, null,
            React.createElement("div", { className: styles.stack },
                React.createElement(Card.Card, null,
                    React.createElement(LocationLedger, { path: path !== null && path !== void 0 ? path : "", searchParamEntries: searchParamEntries, fileName: selectedFileName, isExplorerVisible: showFolderContents, onToggleExplorer: () => setShowFolderContents((prev) => !prev) }),
                    showFolderContents && (React.createElement(Card.Content, { flush: true },
                        React.createElement("div", { className: styles["column-headers"] },
                            React.createElement("div", { className: styles["item-name"] }, "Name"),
                            React.createElement("div", { className: styles["item-details"] },
                                React.createElement("div", { className: styles["file-type"] }, "Type"),
                                React.createElement("div", { className: styles["file-size"] }, "Size"),
                                React.createElement("div", { className: styles["quality-status"] }, "Status"))),
                        React.createElement(QueryResult, { status: getQueryStatus({
                                isLoading,
                                isFetching,
                                isError,
                                error,
                                data: folderContents,
                            }), fetchingClassName: styles["fetching-indicator"] },
                            React.createElement("div", { className: styles["virtuoso-wrapper"] },
                                (folderContents === null || folderContents === void 0 ? void 0 : folderContents.items) && (React.createElement(Virtuoso, { key: path !== null && path !== void 0 ? path : "", style: {
                                        height,
                                    }, totalCount: folderContents === null || folderContents === void 0 ? void 0 : folderContents.items.length, initialTopMostItemIndex: fileId ? getIndexByFileId(fileId, folderContents) : 0, rangeChanged: (range) => {
                                        handleCounterChange(range);
                                    }, atBottomStateChange: (atBottom) => {
                                        handleHtmlElementVisibilityChange(fadeOverlayRef.current, !atBottom);
                                    }, itemContent: (index) => {
                                        var _a;
                                        const item = folderContents.items[index];
                                        return (React.createElement(BrowserItem, { key: `${item.name}-${item.type}-${(_a = item.file) === null || _a === void 0 ? void 0 : _a.id}`, item: item, path: path !== null && path !== void 0 ? path : "", searchParamEntries: searchParamEntries, index: index }));
                                    } })),
                                React.createElement("div", { ref: fadeOverlayRef, className: styles["virtuoso-bottom-overlay"] }, itemCount > 0 && (React.createElement("span", { ref: itemsCounterRef, className: styles["items-counter"] }, `Showing 1-${Math.min(maxVisibleItems, itemCount)} out of ${itemCount} items`)))))))),
                fileId !== undefined ? (React.createElement(Outlet, null)) : (React.createElement(Card.Card, null,
                    React.createElement(Card.Content, null,
                        React.createElement(EmptyState, { subject: "file selected", hint: "Pick a file in the explorer to open its analysis here." }))))))));
};
export default FileBrowser;
//# sourceMappingURL=FileBrowser.js.map